/**
 * SidebarUtilityDock - Bottom utility row in the sidebar
 *
 * Small icon buttons for incidental configuration (Desktop debug UI, Debug page).
 * Keyword configuration now lives inside the Keyword Flags dashboard.
 */

import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebRTC } from '../context/WebRTCContext';
import { APP_VERSION } from '../constants/appMeta';
import './SidebarUtilityDock.css';

const SidebarUtilityDock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendFeatureMessage, isConnected } = useWebRTC();

  const handleDesktopDebugClick = useCallback(() => {
    if (sendFeatureMessage) {
      sendFeatureMessage({
        feature: 'window_visibility',
        cmd: 'show',
        payload: {}
      });
    }
  }, [sendFeatureMessage]);

  const handleDebugClick = useCallback(() => {
    navigate('/app/system/debug');
  }, [navigate]);

  const isDebugActive = location.pathname === '/app/system/debug';

  return (
    <div className="utility-dock">
      <div className="utility-dock__buttons">
        {/* Open desktop debug UI */}
        <button
          className="utility-dock__btn"
          onClick={handleDesktopDebugClick}
          data-tooltip="Open debug UI on desktop"
          aria-label="Open debug UI on desktop"
          disabled={!isConnected}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="12" height="9" rx="1.5"/>
            <path d="M5.5 14h5M8 11v3"/>
            <path d="M5 5.5l2.5 2L5 9.5"/>
          </svg>
        </button>

        {/* Debug tools page (destructive DB ops, "DEV ONLY") — dev builds only.
            The route still resolves by URL; it's just not surfaced in the prod
            sidebar dock, so operators can't stumble into "Delete All Events". */}
        {import.meta.env.DEV && (
          <button
            className={`utility-dock__btn utility-dock__btn--subtle ${isDebugActive ? 'utility-dock__btn--active' : ''}`}
            onClick={handleDebugClick}
            data-tooltip="Debug tools (dev)"
            aria-label="Debug tools"
            aria-current={isDebugActive ? 'page' : undefined}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L4.5 4" />
              <path d="M10 2l1.5 2" />
              <rect x="4" y="4" width="8" height="8" rx="2" />
              <path d="M1 7h3" />
              <path d="M12 7h3" />
              <path d="M1 10h3" />
              <path d="M12 10h3" />
              <path d="M8 4v8" />
            </svg>
          </button>
        )}
      </div>

      <span className="utility-dock__version">v{APP_VERSION}</span>
    </div>
  );
};

export default SidebarUtilityDock;
